/**
 * DatePicker — compact DD/MM/YYYY type-to-fill input. Replaces native
 * <input type="date">, whose segmented cells refuse a typed or pasted Indian
 * date ("23/07/2026", "23 July 2026") and force the browser calendar. Same
 * data contract as the native input: value/onChange are ISO "YYYY-MM-DD".
 *
 * The calendar is not removed — it moves to a button, so both routes work.
 * Mirrors TimePicker's ref discipline for the same reason documented there.
 */

import React, { useEffect, useRef, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';

const pad = (n) => String(n).padStart(2, '0');
const digits = (s, max) => (s || '').replace(/\D+/g, '').slice(0, max);

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** Days in a given month, honouring leap years. 0 when either part is unusable. */
const daysInMonth = (month, year) => {
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (Number.isNaN(m) || m < 1 || m > 12) return 0;
  if (Number.isNaN(y)) return 31; // year not typed yet — don't clamp prematurely
  return new Date(y, m, 0).getDate();
};

/** ISO "YYYY-MM-DD" -> { day, month, year }. Blank parts when unparseable. */
function parseISO(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
  if (!m) return { day: '', month: '', year: '' };
  return { year: m[1], month: m[2], day: m[3] };
}

/** { day, month, year } -> ISO "YYYY-MM-DD", or '' while incomplete/invalid. */
function toISO({ day, month, year }) {
  const d = parseInt(day, 10);
  const mo = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (Number.isNaN(d) || Number.isNaN(mo) || Number.isNaN(y)) return '';
  if (String(year).length !== 4 || y < 1900 || y > 2200) return '';
  if (mo < 1 || mo > 12) return '';
  if (d < 1 || d > daysInMonth(month, year)) return '';
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/**
 * Parse a whole date a user typed or pasted in one go. Accepts the formats an
 * Indian user actually produces — 23/07/2026, 23-7-26, 2026-07-23,
 * "23 July 2026", "23 Jul 26". Returns null when nothing usable is found.
 */
export function parseLooseDate(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;

  // ISO first — unambiguous.
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (iso) return normalise(iso[3], iso[2], iso[1]);

  // Day-first numeric: 23/07/2026, 23-7-26. Day-first is the Indian convention,
  // so an ambiguous 03/04/2026 reads as 3 April, never 4 March.
  const dmy = /^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{2,4})$/.exec(s);
  if (dmy) return normalise(dmy[1], dmy[2], dmy[3]);

  // Named month: "23 July 2026", "23 Jul 26", "July 23, 2026".
  const named = /^(\d{1,2})\s*[-/,\s]\s*([A-Za-z]{3,})\.?\s*[-/,\s]\s*(\d{2,4})$/.exec(s);
  if (named) {
    const mi = MONTHS.indexOf(named[2].slice(0, 3).toLowerCase());
    if (mi >= 0) return normalise(named[1], mi + 1, named[3]);
  }
  const namedFirst = /^([A-Za-z]{3,})\.?\s*[-/,\s]\s*(\d{1,2})\s*[-/,\s]\s*(\d{2,4})$/.exec(s);
  if (namedFirst) {
    const mi = MONTHS.indexOf(namedFirst[1].slice(0, 3).toLowerCase());
    if (mi >= 0) return normalise(namedFirst[2], mi + 1, namedFirst[3]);
  }
  return null;
}

/** Two-digit years become 20xx — a resolution is never dated 0026. */
function normalise(day, month, year) {
  const y = String(year).length <= 2 ? 2000 + parseInt(year, 10) : parseInt(year, 10);
  const d = parseInt(day, 10);
  const mo = parseInt(month, 10);
  if (Number.isNaN(d) || Number.isNaN(mo) || Number.isNaN(y)) return null;
  if (mo < 1 || mo > 12 || d < 1 || d > new Date(y, mo, 0).getDate()) return null;
  return { day: pad(d), month: pad(mo), year: String(y) };
}

export default function DatePicker({
  value = '',
  onChange,
  disabled = false,
  className = '',
  ariaLabel = 'Date',
}) {
  const initial = parseISO(value);
  const [day, setDay] = useState(initial.day);
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);
  const nativeRef = useRef(null);

  // Latest-value refs. A synchronous focus() fires the previous field's blur
  // BEFORE setState flushes, so every callback reads these, never the closure.
  const dayValRef = useRef(initial.day);
  const monthValRef = useRef(initial.month);
  const yearValRef = useRef(initial.year);

  const setDayValue = (v) => { dayValRef.current = v; setDay(v); };
  const setMonthValue = (v) => { monthValRef.current = v; setMonth(v); };
  const setYearValue = (v) => { yearValRef.current = v; setYear(v); };

  const isEditing = () => {
    const a = document.activeElement;
    return a === dayRef.current || a === monthRef.current || a === yearRef.current;
  };

  // Adopt upstream changes (Restore Draft, an AI patch), but never while the
  // user is mid-edit — that would rewrite the digits under their cursor.
  useEffect(() => {
    if (isEditing()) return;
    const up = parseISO(value);
    setDayValue(up.day);
    setMonthValue(up.month);
    setYearValue(up.year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (next = {}) => {
    const iso = toISO({
      day: dayValRef.current,
      month: monthValRef.current,
      year: yearValRef.current,
      ...next,
    });
    // Emit only a complete, real date. A half-typed date must not clobber a
    // saved one — the same rule TimePicker follows.
    if (iso) onChange?.(iso);
  };

  const applyWhole = (parsed) => {
    setDayValue(parsed.day);
    setMonthValue(parsed.month);
    setYearValue(parsed.year);
    onChange?.(`${parsed.year}-${parsed.month}-${parsed.day}`);
  };

  // A pasted or typed-in-one-go date lands in whichever box has focus; parse it
  // there rather than making the user split it across three fields.
  const handlePaste = (e) => {
    const parsed = parseLooseDate(e.clipboardData?.getData('text'));
    if (!parsed) return;
    e.preventDefault();
    applyWhole(parsed);
    yearRef.current?.blur();
  };

  const handleDayChange = (raw) => {
    // Typing separators into the day box means a whole date is being entered.
    const whole = parseLooseDate(raw);
    if (whole) { applyWhole(whole); return; }
    const v = digits(raw, 2);
    setDayValue(v);
    // Auto-advance on 2 digits, or on a leading digit that cannot start a valid
    // 2-digit day (4-9 → there is no 40th).
    if ((v.length === 2 || (v.length === 1 && parseInt(v, 10) > 3)) && monthRef.current) {
      monthRef.current.focus();
      monthRef.current.select();
    }
    commit({ day: v });
  };

  const handleMonthChange = (raw) => {
    const v = digits(raw, 2);
    setMonthValue(v);
    if ((v.length === 2 || (v.length === 1 && parseInt(v, 10) > 1)) && yearRef.current) {
      yearRef.current.focus();
      yearRef.current.select();
    }
    commit({ month: v });
  };

  const handleYearChange = (raw) => {
    const v = digits(raw, 4);
    setYearValue(v);
    commit({ year: v });
  };

  const handleDayBlur = () => {
    const cur = dayValRef.current;
    if (!cur) return; // empty stays empty — never coerce to today
    const n = parseInt(cur, 10);
    if (Number.isNaN(n) || n < 1) { setDayValue(''); return; }
    const max = daysInMonth(monthValRef.current, yearValRef.current) || 31;
    const padded = pad(Math.min(n, max));
    setDayValue(padded);
    commit({ day: padded });
  };

  const handleMonthBlur = () => {
    const cur = monthValRef.current;
    if (!cur) return;
    const n = parseInt(cur, 10);
    if (Number.isNaN(n) || n < 1) { setMonthValue(''); return; }
    const padded = pad(Math.min(n, 12));
    setMonthValue(padded);
    // Re-clamp the day: 31 typed before month became February is now the 28th.
    const max = daysInMonth(padded, yearValRef.current);
    const d = parseInt(dayValRef.current, 10);
    if (max && !Number.isNaN(d) && d > max) {
      const clamped = pad(max);
      setDayValue(clamped);
      commit({ month: padded, day: clamped });
      return;
    }
    commit({ month: padded });
  };

  const handleYearBlur = () => {
    const cur = yearValRef.current;
    if (!cur) return;
    // "26" means 2026. Nobody dates a board resolution in the year 26.
    const expanded = cur.length <= 2 ? String(2000 + parseInt(cur, 10)) : cur;
    if (expanded.length !== 4) { setYearValue(expanded); return; }
    setYearValue(expanded);
    const max = daysInMonth(monthValRef.current, expanded);
    const d = parseInt(dayValRef.current, 10);
    if (max && !Number.isNaN(d) && d > max) {
      const clamped = pad(max);
      setDayValue(clamped);
      commit({ year: expanded, day: clamped });
      return;
    }
    commit({ year: expanded });
  };

  const handleKeyDown = (e, which) => {
    // A separator advances to the next box, so "23/7/2026" types straight through.
    if ((e.key === '/' || e.key === '-' || e.key === '.' || e.key === ' ') && which !== 'year') {
      e.preventDefault();
      const next = which === 'day' ? monthRef.current : yearRef.current;
      next?.focus();
      next?.select();
      return;
    }
    if (e.key === 'Backspace' && which === 'month' && !monthValRef.current) {
      e.preventDefault();
      dayRef.current?.focus();
      return;
    }
    if (e.key === 'Backspace' && which === 'year' && !yearValRef.current) {
      e.preventDefault();
      monthRef.current?.focus();
    }
  };

  // The calendar is still available — it just no longer blocks typing.
  const openCalendar = () => {
    if (disabled) return;
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch { /* falls through to focus */ }
    }
    el.focus();
    el.click();
  };

  const box = 'text-center bg-transparent outline-none text-[#08111f] placeholder:text-[#c0b8a8]';

  return (
    <div
      className={`flex items-center justify-between gap-2 border-[1.5px] rounded-[8px] px-[11px] py-2 bg-white transition-colors focus-within:border-[#1e8a8a] ${disabled ? 'opacity-60' : ''} ${className}`}
      style={{ borderColor: '#ddd6c6' }}
    >
      <div
        className="flex items-center gap-1.5 min-w-0"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        <div className="flex items-center">
          <input
            ref={dayRef}
            type="text"
            inputMode="numeric"
            maxLength={10}
            disabled={disabled}
            value={day}
            onChange={(e) => handleDayChange(e.target.value)}
            onBlur={handleDayBlur}
            onKeyDown={(e) => handleKeyDown(e, 'day')}
            onFocus={(e) => e.target.select()}
            onPaste={handlePaste}
            placeholder="DD"
            aria-label={`${ariaLabel} — day`}
            className={`w-[22px] text-[12.5px] ${box}`}
          />
          <span className="text-[12.5px] text-[#7a7060]">/</span>
          <input
            ref={monthRef}
            type="text"
            inputMode="numeric"
            maxLength={2}
            disabled={disabled}
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            onBlur={handleMonthBlur}
            onKeyDown={(e) => handleKeyDown(e, 'month')}
            onFocus={(e) => e.target.select()}
            onPaste={handlePaste}
            placeholder="MM"
            aria-label={`${ariaLabel} — month`}
            className={`w-[22px] text-[12.5px] ${box}`}
          />
          <span className="text-[12.5px] text-[#7a7060]">/</span>
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            maxLength={4}
            disabled={disabled}
            value={year}
            onChange={(e) => handleYearChange(e.target.value)}
            onBlur={handleYearBlur}
            onKeyDown={(e) => handleKeyDown(e, 'year')}
            onFocus={(e) => e.target.select()}
            onPaste={handlePaste}
            placeholder="YYYY"
            aria-label={`${ariaLabel} — year`}
            className={`w-[36px] text-[12.5px] ${box}`}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={openCalendar}
        disabled={disabled}
        title="Pick from calendar"
        aria-label="Open calendar"
        className="shrink-0 p-[3px] rounded-[5px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#f6f3ea]"
      >
        <FiCalendar className="text-[12px] text-[#7a7060]" />
      </button>

      {/* Native input kept only as the calendar surface — never focusable by tab,
          never the thing the user types into. */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={toISO({ day, month, year }) || ''}
        onChange={(e) => {
          const p = parseISO(e.target.value);
          if (p.day) applyWhole(p);
        }}
        className="sr-only absolute w-0 h-0 opacity-0 pointer-events-none"
      />
    </div>
  );
}
